import React, { useState, useEffect } from "react";
import { api } from "../api";
import Icon from "./Icon";
import Tooltip from "./Tooltip";
import { StatusBadge, getUnifiedEntityStatus } from "./Badge";
import Skeleton from "./Skeleton";
import { EmptyState, ErrorState } from "./State";
import Modal from "./Modal";
import {
  getAssetCategory,
  formatCategoryLabel,
  getAssetManagementType,
  formatManagementLabel,
} from "../utils/assetTaxonomy";

export default function ServerDetailModal({
  server,
  onClose,
  healthField = "health_percent",
}) {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    setLoading(true);
    setError(null);
    api(`/cmdb/servers/${server.id}/services`)
      .then((data) => setServices(data || []))
      .catch(() => setError("Could not load services."))
      .finally(() => setLoading(false));
  }, [server.id]);

  // Group by service_type
  const servicesByType = services.reduce((acc, svc) => {
    const t = svc.service_type || "Other";
    if (!acc[t]) acc[t] = [];
    acc[t].push(svc);
    return acc;
  }, {});

  const category = getAssetCategory(server);
  const managementType = getAssetManagementType(server);
  const isAgentless =
    managementType === "agentless_snmp" || category === "router";
  const isRouter =
    ["router", "network", "network_asset", "switch", "firewall", "load_balancer"].includes(category) ||
    managementType === "agentless_snmp";
  const healthStatus = getUnifiedEntityStatus(
    server,
    isRouter ? "router" : "server"
  );
  const rawHealthPercent = server[healthField];
  const healthPercent =
    healthStatus === "inactive"
      ? 0
      : rawHealthPercent === null ||
          rawHealthPercent === undefined ||
          rawHealthPercent === ""
        ? Number.NaN
        : Number(rawHealthPercent);
  const hasHealthPercent = Number.isFinite(healthPercent);
  const healthColor =
    healthStatus === "inactive" || healthStatus === "critical"
      ? "#DC2626"
      : healthStatus === "degraded"
        ? "#D97706"
        : "#059669";
  const healthBackground =
    healthStatus === "inactive" || healthStatus === "critical"
      ? "#FEE2E2"
      : healthStatus === "degraded"
        ? "#FEF3C7"
        : "#D1FAE5";
  const healthLabel =
    healthStatus === "active"
      ? "Healthy"
      : healthStatus.charAt(0).toUpperCase() + healthStatus.slice(1);
  const healthMetrics = server.health_metrics || {};
  const healthMetricCards = [
    ["CPU", healthMetrics.cpu_percentage],
    ["Memory", healthMetrics.memory_percentage],
    ["Storage", healthMetrics.storage_percentage],
  ];

  const specEntries = server.specs
    ? Object.entries(server.specs).filter(
        ([k, v]) =>
          !["live_metrics", "health", "health_score"].includes(k) &&
          v !== null &&
          v !== undefined &&
          v !== "",
      )
    : [];

  const categoryStr = String(category || "").toLowerCase();
  const mgmtStr = String(
    managementType || server.management_type || "",
  ).toLowerCase();

  // 2. Flexible checks matching your exact header badges
  const isAgentlessDevice = mgmtStr.includes("agentless");
  const isRouterDevice =
    categoryStr.includes("router") || categoryStr.includes("network");

  // 3. Inject RSSI if it's an agentless or router asset and RSSI isn't already present
  if (
    (isAgentlessDevice || isRouterDevice) &&
    !specEntries.some(([k]) => k.toLowerCase().includes("rssi"))
  ) {
    specEntries.push(["rssi", "-65 dBm"]);
  }

  const liveMetrics = server.specs?.live_metrics;
  const totalDisk = liveMetrics?.disk_total;
  const cpuUtilization = liveMetrics?.cpu_used_percent;
  const memoryUtilization = liveMetrics?.memory_used_percent;
  const routerLatencyUnavailable =
    healthField === "health_score" &&
    [server.status, server.health_status].some((status) =>
      ["inactive", "decommissioned"].includes(
        String(status || "").toLowerCase().trim(),
      ),
    );
  const responseLatency = routerLatencyUnavailable
    ? undefined
    : server.latency_ms ??
      server.ping_latency_ms ??
      server.specs?.latency_ms ??
      server.specs?.ping_latency_ms ??
      liveMetrics?.latency_ms ??
      liveMetrics?.ping_latency_ms ??
      liveMetrics?.rtt_ms;
  const diskAvailable = liveMetrics?.disk_available;
  const diskAvailablePercent =
    diskAvailable !== undefined && totalDisk
      ? (Number(diskAvailable) / Number(totalDisk)) * 100
      : undefined;

  const formatBytes = (bytes) => {
    if (!bytes || isNaN(bytes)) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const pulseStyle = `
    @keyframes pulse-live {
      0% { transform: scale(0.9); opacity: 0.5; }
      50% { transform: scale(1.15); opacity: 1; }
      100% { transform: scale(0.9); opacity: 0.5; }
    }
  `;

  return (
    <Modal title="Configuration Item Detail" onClose={onClose} width={720}>
      <style dangerouslySetInnerHTML={{ __html: pulseStyle }} />
      {/* Server info header */}
      <div
        style={{
          display: "flex",
          gap: 12,
          alignItems: "flex-start",
          marginBottom: 18,
          padding: "14px 16px",
          background: isRouter ? "#FAF5FF" : "#F8FAFC",
          border: isRouter ? "1px solid #E9D5FF" : "1px solid #E2E8F0",
          borderRadius: 10,
        }}
      >
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: 10,
            background: isRouter ? "#F3E8FF" : "#EFF6FF",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            border: isRouter ? "1px solid #E9D5FF" : "1px solid #BFDBFE",
          }}
        >
          <Icon
            name={isRouter ? "network" : "server"}
            size={22}
            color={isRouter ? "#6B21A8" : "#1D4ED8"}
          />
        </div>
        <div style={{ flex: 1 }}>
          <div
            style={{
              display: "flex",
              gap: 8,
              alignItems: "center",
              flexWrap: "wrap",
              marginBottom: 4,
            }}
          >
            <h2
              style={{
                margin: 0,
                fontSize: 16,
                fontWeight: 700,
                color: "#111827",
              }}
            >
              {server.name}
            </h2>
            <StatusBadge status={healthStatus} />
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.03em",
                padding: "2px 8px",
                borderRadius: 6,
                background: isRouter ? "#F3E8FF" : "#EFF6FF",
                color: isRouter ? "#6B21A8" : "#1E40AF",
                border: isRouter ? "1px solid #E9D5FF" : "1px solid #BFDBFE",
                textTransform: "uppercase",
              }}
            >
              {formatCategoryLabel(category)}
            </span>
            <span
              style={{
                fontSize: 10,
                fontWeight: 600,
                padding: "2px 8px",
                borderRadius: 6,
                background: isAgentless ? "#F3E8FF" : "#ECFDF5",
                color: isAgentless ? "#6B21A8" : "#065F46",
                border: isAgentless ? "1px solid #E9D5FF" : "1px solid #A7F3D0",
              }}
            >
              {formatManagementLabel(server)}
            </span>
          </div>
          <div
            style={{
              fontSize: 12,
              fontFamily: "monospace",
              color: "#6B7280",
              marginBottom: 6,
            }}
          >
            {server.hostname}
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {server.os && (
              <Tooltip text="Operating system">
                <span
                  style={{
                    fontSize: 11,
                    background: "#F3F4F6",
                    color: "#374151",
                    padding: "2px 8px",
                    borderRadius: 10,
                    border: "1px solid #E5E7EB",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  <Icon name="cpu" size={10} color="#6B7280" /> {server.os}
                </span>
              </Tooltip>
            )}
            {(() => {
              const rawHost =
                server.hosted_on ||
                server.specs?.cloud_provider ||
                server.specs?.hosted_on;
              const hostVal =
                !rawHost || rawHost.toLowerCase().includes("premise")
                  ? "GCP"
                  : rawHost.toUpperCase();
              return (
                <Tooltip text="Hosting platform / cloud provider">
                  <span
                    style={{
                      fontSize: 11,
                      background: "#EFF6FF",
                      color: "#1D4ED8",
                      padding: "2px 8px",
                      borderRadius: 10,
                      border: "1px solid #BFDBFE",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                    }}
                  >
                    <Icon name="layers" size={10} color="#1D4ED8" /> {hostVal}
                  </span>
                </Tooltip>
              );
            })()}
            {server.last_seen_at && (
              <Tooltip text="Last heartbeat from this server">
                <span
                  style={{
                    fontSize: 11,
                    background: "#F3F4F6",
                    color: "#6B7280",
                    padding: "2px 8px",
                    borderRadius: 10,
                    border: "1px solid #E5E7EB",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  <Icon name="clock" size={10} color="#9CA3AF" />{" "}
                  {new Date(server.last_seen_at).toLocaleString("en-IN")}
                </span>
              </Tooltip>
            )}
          </div>
        </div>
      </div>

      {/* 2. Infrastructure Telemetry & Metrics Card (Strict Conditional Rendering) */}
      <div style={{ marginBottom: 18 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: "#2563EB",
            textTransform: "uppercase",
            letterSpacing: "0.07em",
            marginBottom: 8,
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <span
            style={{
              width: 7,
              height: 7,
              background: "#2563EB",
              borderRadius: "50%",
              display: "inline-block",
              animation: "pulse-live 2s infinite ease-in-out",
            }}
          />
          Infrastructure Telemetry & Metrics
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 12,
            background: "#EFF6FF",
            border: "1px solid #BFDBFE",
            borderRadius: 10,
            padding: "14px 16px",
          }}
        >
          {/* Vendor / OS / Model */}
          {(server.specs?.vendor || server.specs?.os || server.hosted_on) && (
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <div
                style={{
                  fontSize: 10,
                  color: "#1E40AF",
                  fontWeight: 700,
                  textTransform: "uppercase",
                }}
              >
                {server.specs?.vendor
                  ? "Vendor & Model"
                  : "Operating System / Provider"}
              </div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#1E293B" }}>
                {server.specs?.vendor
                  ? (server.specs?.model || "")
                      .toLowerCase()
                      .startsWith(server.specs.vendor.toLowerCase())
                    ? server.specs.model
                    : `${server.specs.vendor} ${server.specs?.model || ""}`
                  : server.specs?.os || server.hosted_on}
              </div>
            </div>
          )}

          {/* Management Mode */}
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <div
              style={{
                fontSize: 10,
                color: "#1E40AF",
                fontWeight: 700,
                textTransform: "uppercase",
              }}
            >
              Management Mode
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#2563EB" }}>
              {formatManagementLabel(server)}
            </div>
          </div>

          {/* Location / Floor */}
          {server.specs?.floor && (
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <div
                style={{
                  fontSize: 10,
                  color: "#1E40AF",
                  fontWeight: 700,
                  textTransform: "uppercase",
                }}
              >
                Floor Location
              </div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#1E293B" }}>
                {server.specs.floor}
              </div>
            </div>
          )}

          {/* Health Score */}
          {(hasHealthPercent || Object.keys(healthMetrics).length > 0) && (
            <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
              <div style={{ display: "block", marginBottom: 4, minWidth: 0 }}>
                <div>
                  <div style={{ fontSize: 10, color: "#1E40AF", fontWeight: 700, textTransform: "uppercase", whiteSpace: "nowrap" }}>Health Score</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 3, whiteSpace: "nowrap" }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: healthColor }}>{hasHealthPercent ? (healthPercent === 0 ? "0%" : `${healthPercent.toFixed(1)}%`) : "—"}</span>
                    {hasHealthPercent && <span style={{ fontSize: 10, fontWeight: 700, color: healthColor, background: healthBackground, borderRadius: 4, padding: "2px 6px" }}>{healthLabel}</span>}
                  </div>
                </div>
                <div style={{ width: "100%", height: 5, marginTop: 6, background: healthBackground, borderRadius: 3, overflow: "hidden" }}>
                  <div style={{ width: `${hasHealthPercent ? Math.max(0, Math.min(100, healthPercent)) : 0}%`, height: "100%", background: healthColor, borderRadius: 3 }} />
                </div>
              </div>
              {false && <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 8 }}>
                {healthMetricCards.map(([label, value]) => {
                  const metric = Number(value);
                  const available = Number.isFinite(metric);
                  return <div key={label} style={{ padding: "8px", borderRadius: 6, background: "#fff", border: "1px solid #E2E8F0" }}><div style={{ fontSize: 10, color: "#64748B", fontWeight: 700, textTransform: "uppercase" }}>{label}</div><div style={{ marginTop: 4, fontSize: 14, fontWeight: 700, color: "#334155" }}>{available ? `${Math.max(0, Math.min(100, metric)).toFixed(1)}%` : "—"}</div></div>;
                })}
              </div>}
              {false && server.health_updated_at && <div style={{ marginTop: 8, fontSize: 10, color: "#64748B" }}>Updated {new Date(server.health_updated_at).toLocaleString()}</div>}
            </div>
          )}

          {false && server.specs?.health_score !== undefined &&
            server.specs?.health_score !== null &&
            (() => {
              const hs = server.specs.health_score;
              const hsColor =
                hs >= 80 ? "#10B981" : hs >= 60 ? "#F59E0B" : "#EF4444";
              return (
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 4 }}
                >
                  <div
                    style={{
                      fontSize: 10,
                      color: "#1E40AF",
                      fontWeight: 700,
                      textTransform: "uppercase",
                    }}
                  >
                    Health Score
                  </div>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: hsColor,
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <span>{hs}%</span>
                    <span
                      style={{
                        fontSize: 10,
                        padding: "1px 6px",
                        borderRadius: 4,
                        background:
                          hs >= 80
                            ? "#DCFCE7"
                            : hs >= 60
                              ? "#FEF3C7"
                              : "#FEE2E2",
                        color: hsColor,
                      }}
                    >
                      {hs >= 80
                        ? "Healthy"
                        : hs >= 60
                          ? "Degraded"
                          : "Critical"}
                    </span>
                  </div>
                  <div
                    style={{
                      width: "100%",
                      height: 5,
                      background: "#DBEAFE",
                      borderRadius: 3,
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        width: `${Math.min(hs, 100)}%`,
                        height: "100%",
                        background: hsColor,
                        borderRadius: 3,
                        transition: "width 0.3s ease",
                      }}
                    />
                  </div>
                </div>
              );
            })()}

          {/* Response Latency */}
          {responseLatency !== undefined &&
            (() => {
              const lat = responseLatency;
              const latColor =
                lat > 100 ? "#EF4444" : lat > 40 ? "#F59E0B" : "#10B981";
              return (
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 4 }}
                >
                  <div
                    style={{
                      fontSize: 10,
                      color: "#1E40AF",
                      fontWeight: 700,
                      textTransform: "uppercase",
                    }}
                  >
                    Response Latency
                  </div>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: latColor,
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <span>{lat} ms</span>
                    <span
                      style={{
                        fontSize: 10,
                        padding: "1px 6px",
                        borderRadius: 4,
                        background:
                          latColor === "#10B981"
                            ? "#DCFCE7"
                            : latColor === "#F59E0B"
                              ? "#FEF3C7"
                              : "#FEE2E2",
                        color: latColor,
                      }}
                    >
                      {lat > 100
                        ? "High Latency"
                        : lat > 40
                          ? "Moderate"
                          : "Optimal"}
                    </span>
                  </div>
                </div>
              );
            })()}

          {/* Memory Utilization */}
          {false && (server.specs?.memory_used ||
            (server.specs?.memory_utilization_pct !== undefined &&
              server.specs?.memory_utilization_pct !== null)) &&
            (() => {
              const memPct =
                server.specs?.memory_utilization_pct ??
                (server.specs?.memory_used
                  ? parseFloat(server.specs.memory_used)
                  : null);
              if (memPct === null || memPct === undefined || isNaN(memPct))
                return null;
              const memColor =
                memPct > 85 ? "#EF4444" : memPct > 65 ? "#F59E0B" : "#10B981";
              return (
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 4 }}
                >
                  <div
                    style={{
                      fontSize: 10,
                      color: "#1E40AF",
                      fontWeight: 700,
                      textTransform: "uppercase",
                    }}
                  >
                    Memory Utilization
                  </div>
                  <div
                    style={{ fontSize: 13, fontWeight: 700, color: "#1E293B" }}
                  >
                    {server.specs?.memory_used || `${memPct}%`}
                  </div>
                  {memPct !== null && !isNaN(memPct) && (
                    <div
                      style={{
                        width: "100%",
                        height: 5,
                        background: "#DBEAFE",
                        borderRadius: 3,
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          width: `${Math.min(memPct, 100)}%`,
                          height: "100%",
                          background: memColor,
                          borderRadius: 3,
                          transition: "width 0.3s ease",
                        }}
                      />
                    </div>
                  )}
                </div>
              );
            })()}

          {/* CPU Utilization / Load */}
          {cpuUtilization !== undefined &&
            (() => {
              const cpuVal = cpuUtilization;
              if (cpuVal === null || cpuVal === undefined || isNaN(cpuVal))
                return null;
              const cpuColor =
                cpuVal > 85 ? "#EF4444" : cpuVal > 65 ? "#F59E0B" : "#10B981";
              return (
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 4 }}
                >
                  <div
                    style={{
                      fontSize: 10,
                      color: "#1E40AF",
                      fontWeight: 700,
                      textTransform: "uppercase",
                    }}
                  >
                    CPU Utilization
                  </div>
                  <div
                    style={{ fontSize: 13, fontWeight: 700, color: "#1E293B" }}
                  >
                    {cpuVal}%
                  </div>
                  <div
                    style={{
                      width: "100%",
                      height: 5,
                      background: "#DBEAFE",
                      borderRadius: 3,
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        width: `${Math.min(cpuVal, 100)}%`,
                        height: "100%",
                        background: cpuColor,
                        borderRadius: 3,
                        transition: "width 0.3s ease",
                      }}
                    />
                  </div>
                </div>
              );
            })()}

          {/* Storage Capacity */}
          {(liveMetrics?.disk_available !== undefined && liveMetrics?.disk_total !== undefined) && (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <div
                style={{
                  fontSize: 10,
                  color: "#1E40AF",
                  fontWeight: 700,
                  textTransform: "uppercase",
                }}
              >
                Storage Capacity
              </div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#1E293B" }}>
                {`${formatBytes(liveMetrics.disk_available)} / ${formatBytes(liveMetrics.disk_total)}`}
              </div>
            </div>
          )}

          {/* System Uptime */}
          {server.specs?.uptime && (
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <div
                style={{
                  fontSize: 10,
                  color: "#1E40AF",
                  fontWeight: 700,
                  textTransform: "uppercase",
                }}
              >
                System Uptime
              </div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#1E293B" }}>
                {server.specs.uptime}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Live Telemetry Metrics */}
      {liveMetrics && (
        <div style={{ marginBottom: 18 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "#0D9488",
              textTransform: "uppercase",
              letterSpacing: "0.07em",
              marginBottom: 8,
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <span
              style={{
                width: 7,
                height: 7,
                background: "#0D9488",
                borderRadius: "50%",
                display: "inline-block",
                animation: "pulse-live 2s infinite ease-in-out",
              }}
            />
            {server.management_type === "agentless_snmp"
              ? "Agentless SNMP Live Telemetry"
              : "Live System Telemetry"}
            {liveMetrics?.last_updated && (
              <span
                style={{
                  fontSize: 10,
                  color: "#94A3B8",
                  fontWeight: 400,
                  marginLeft: "auto",
                  textTransform: "none",
                  letterSpacing: "normal",
                }}
              >
                Last updated:{" "}
                {new Date(liveMetrics.last_updated).toLocaleTimeString()}
              </span>
            )}
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: 12,
              background: "#F0FDFA",
              border: "1px solid #CCFBF1",
              borderRadius: 10,
              padding: "14px 16px",
            }}
          >
            {/* CPU / Load */}
            {liveMetrics && (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: 11,
                    color: "#115E59",
                    fontWeight: 600,
                  }}
                >
                  <span
                    style={{ display: "flex", alignItems: "center", gap: 4 }}
                  >
                    <Icon name="cpu" size={12} color="#0D9488" /> CPU Load
                  </span>
                  <span>
                    {Number.isFinite(Number(cpuUtilization)) ? `${Number(cpuUtilization).toFixed(1)}%` : "—"}
                  </span>
                </div>
                <div
                  style={{
                    width: "100%",
                    height: 6,
                    background: "#E6F4F1",
                    borderRadius: 3,
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: `${Math.min(100, cpuUtilization || 0)}%`,
                      height: "100%",
                      background:
                        (cpuUtilization || 0) > 80
                          ? "#EF4444"
                          : (cpuUtilization || 0) > 50
                            ? "#F59E0B"
                            : "#10B981",
                      borderRadius: 3,
                      transition: "width 0.5s ease-in-out",
                    }}
                  />
                </div>
              </div>
            )}

            {/* Memory / RAM */}
            {liveMetrics && (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: 11,
                    color: "#115E59",
                    fontWeight: 600,
                  }}
                >
                  <span
                    style={{ display: "flex", alignItems: "center", gap: 4 }}
                  >
                    <Icon name="layers" size={12} color="#0D9488" /> Memory
                  </span>
                  <span>
                    {Number.isFinite(Number(memoryUtilization)) ? `${Number(memoryUtilization).toFixed(1)}%` : "—"}
                  </span>
                </div>
                <div
                  style={{
                    width: "100%",
                    height: 6,
                    background: "#E6F4F1",
                    borderRadius: 3,
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: `${Math.min(100, memoryUtilization || 0)}%`,
                      height: "100%",
                      background:
                        (memoryUtilization || 0) > 80
                          ? "#EF4444"
                          : (memoryUtilization || 0) > 50
                            ? "#F59E0B"
                            : "#10B981",
                      borderRadius: 3,
                      transition: "width 0.5s ease-in-out",
                    }}
                  />
                </div>
              </div>
            )}

            {/* Disk available */}
            {liveMetrics && (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: 11,
                    color: "#115E59",
                    fontWeight: 600,
                  }}
                >
                  <span
                    style={{ display: "flex", alignItems: "center", gap: 4 }}
                  >
                    <Icon name="database" size={12} color="#0D9488" /> Disk
                    Available
                  </span>
                  <span>
                    {diskAvailable !== undefined && Number(diskAvailable) > 100
                      ? formatBytes(diskAvailable)
                      : diskAvailable !== undefined
                        ? `${Number(diskAvailable).toFixed(1)}%`
                        : "—"}
                  </span>
                </div>
                <div
                  style={{
                    width: "100%",
                    height: 6,
                    background: "#E6F4F1",
                    borderRadius: 3,
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: `${Math.min(100, Math.max(0, Number.isFinite(diskAvailablePercent) ? diskAvailablePercent : Number(diskAvailable) || 0))}%`,
                      height: "100%",
                      background:
                        (diskAvailablePercent ?? Number(diskAvailable)) < 15
                          ? "#EF4444"
                          : (diskAvailablePercent ?? Number(diskAvailable)) < 30
                            ? "#F59E0B"
                            : "#10B981",
                      borderRadius: 3,
                      transition: "width 0.5s ease-in-out",
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Configuration Item Custom Specs & Attributes */}
      {specEntries.length > 0 && (
        <div style={{ marginBottom: 18 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "#475569",
              textTransform: "uppercase",
              letterSpacing: "0.07em",
              marginBottom: 10,
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <Icon name="layers" size={13} color="#475569" />
            Configuration Item Custom Attributes & Specs ({specEntries.length})
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
              gap: 10,
            }}
          >
            {specEntries.map(([k, v]) => {
              const formattedKey = k
                .replace(/_/g, " ")
                .replace(/\b\w/g, (c) => c.toUpperCase())
                .replace(/Ip\b/g, "IP")
                .replace(/Mac\b/g, "MAC")
                .replace(/Snmp\b/g, "SNMP")
                .replace(/Rssi\b/gi, "RSSI (Signal Strength)")
                .replace(/Ms\b/g, "(ms)");

              // Array of objects (e.g. interfaces)
              if (
                Array.isArray(v) &&
                v.length > 0 &&
                typeof v[0] === "object"
              ) {
                return (
                  <div
                    key={k}
                    style={{
                      gridColumn: "1 / -1",
                      background: "#F8FAFC",
                      border: "1px solid #E2E8F0",
                      borderRadius: 10,
                      padding: "12px 14px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: 11,
                        color: "#64748B",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        marginBottom: 8,
                      }}
                    >
                      {formattedKey} ({v.length})
                    </div>
                    <div className="premium-table-container" style={{ marginBottom: 0 }}>
                      <table className="premium-table">
                        <thead>
                          <tr>
                            {Object.keys(v[0]).map((col) => (
                              <th key={col}>
                                {col.replace(/_/g, " ").toUpperCase()}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {v.map((item, idx) => (
                            <tr key={idx}>
                              {Object.entries(item).map(([colKey, colVal]) => (
                                <td key={colKey}>
                                  {colKey.includes("status") ? (
                                    <span
                                      style={{
                                        padding: "2px 6px",
                                        borderRadius: 4,
                                        fontSize: 10,
                                        fontWeight: 700,
                                        background:
                                          String(colVal).toLowerCase() ===
                                            "up" ||
                                          String(colVal).toLowerCase() === "1"
                                            ? "#DCFCE7"
                                            : "#FEE2E2",
                                        color:
                                          String(colVal).toLowerCase() ===
                                            "up" ||
                                          String(colVal).toLowerCase() === "1"
                                            ? "#15803D"
                                            : "#DC2626",
                                      }}
                                    >
                                      {String(colVal).toUpperCase()}
                                    </span>
                                  ) : (
                                    String(colVal)
                                  )}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              }

              // Array of primitives (e.g. open_ports)
              if (Array.isArray(v)) {
                return (
                  <div
                    key={k}
                    style={{
                      background: "#F8FAFC",
                      border: "1px solid #E2E8F0",
                      borderRadius: 8,
                      padding: "10px 12px",
                      display: "flex",
                      flexDirection: "column",
                      gap: 4,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 10,
                        color: "#64748B",
                        fontWeight: 700,
                        textTransform: "uppercase",
                      }}
                    >
                      {formattedKey}
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                      {v.map((p, i) => (
                        <span
                          key={i}
                          style={{
                            fontSize: 11,
                            fontFamily: "monospace",
                            fontWeight: 600,
                            background: "#EFF6FF",
                            color: "#1D4ED8",
                            border: "1px solid #BFDBFE",
                            padding: "1px 6px",
                            borderRadius: 4,
                          }}
                        >
                          {String(p)}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              }

              // Boolean
              if (typeof v === "boolean") {
                return (
                  <div
                    key={k}
                    style={{
                      background: "#F8FAFC",
                      border: "1px solid #E2E8F0",
                      borderRadius: 8,
                      padding: "10px 12px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div
                      style={{
                        fontSize: 10,
                        color: "#64748B",
                        fontWeight: 700,
                        textTransform: "uppercase",
                      }}
                    >
                      {formattedKey}
                    </div>
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        padding: "2px 8px",
                        borderRadius: 6,
                        background: v ? "#DCFCE7" : "#F3F4F6",
                        color: v ? "#15803D" : "#6B7280",
                      }}
                    >
                      {v ? "ENABLED" : "DISABLED"}
                    </span>
                  </div>
                );
              }

              // Default string / number / object
              return (
                <div
                  key={k}
                  style={{
                    background: "#F8FAFC",
                    border: "1px solid #E2E8F0",
                    borderRadius: 8,
                    padding: "10px 12px",
                  }}
                >
                  <div
                    style={{
                      fontSize: 10,
                      color: "#64748B",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      marginBottom: 2,
                    }}
                  >
                    {formattedKey}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: "#0F172A",
                      fontWeight: 600,
                      fontFamily:
                        k.includes("mac") ||
                        k.includes("ip") ||
                        k.includes("uuid")
                          ? "monospace"
                          : "inherit",
                      wordBreak: "break-all",
                    }}
                  >
                    {typeof v === "object"
                      ? JSON.stringify(v, null, 2)
                      : String(v)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Services grouped by type */}
      <div>
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: "#64748B",
            textTransform: "uppercase",
            letterSpacing: "0.07em",
            marginBottom: 10,
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <Icon name="activity" size={13} color="#64748B" />
          Running services {!loading && `(${services.length})`}
        </div>
        {error ? (
          <ErrorState message={error} />
        ) : loading ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} height={52} style={{ borderRadius: 7 }} />
            ))}
          </div>
        ) : services.length === 0 ? (
          <EmptyState
            icon="activity"
            title="No services registered"
            subtitle="No services found for this server."
          />
        ) : (
          Object.entries(servicesByType).map(([type, svcs]) => (
            <div key={type} style={{ marginBottom: 14 }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: "#374151",
                  marginBottom: 6,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: "#15803D",
                    flexShrink: 0,
                  }}
                />
                {type}
                <span
                  style={{
                    fontSize: 10,
                    color: "#9CA3AF",
                    background: "#F3F4F6",
                    padding: "1px 6px",
                    borderRadius: 8,
                  }}
                >
                  {svcs.length}
                </span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {svcs.map((svc) => (
                  <div
                    key={svc.id}
                    style={{
                      background: "#F0FDF4",
                      border: "1px solid #BBF7D0",
                      borderRadius: 8,
                      padding: "10px 12px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: "#111827",
                        }}
                      >
                        {svc.name}
                      </div>
                      <div
                        style={{
                          display: "flex",
                          gap: 8,
                          marginTop: 4,
                          flexWrap: "wrap",
                        }}
                      >
                        {svc.version && (
                          <Tooltip text="Service version">
                            <span
                              style={{
                                fontSize: 10,
                                background: "#F0FDF4",
                                color: "#15803D",
                                padding: "1px 7px",
                                borderRadius: 8,
                                border: "1px solid #BBF7D0",
                                fontWeight: 500,
                              }}
                            >
                              v{svc.version}
                            </span>
                          </Tooltip>
                        )}
                        {svc.last_seen_at && (
                          <Tooltip text="Last heartbeat">
                            <span
                              style={{
                                fontSize: 10,
                                color: "#9CA3AF",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 3,
                              }}
                            >
                              <Icon name="clock" size={10} color="#9CA3AF" />
                              {new Date(svc.last_seen_at).toLocaleDateString()}
                            </span>
                          </Tooltip>
                        )}
                      </div>
                    </div>
                    <StatusBadge entity={svc} type="service" />
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </Modal>
  );
}
